import React, { Key } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { Autocomplete } from '@ds';
import { AdminFormRow } from '@ui';
import { useInfiniteTXCentersListQuery } from '@/queries/tx-centers';
import { renderAutocompleteItem } from '@/utils/helpers';
import { useInfiniteDropdown } from '@/hooks/useInfiniteDropdown';
import { txCenterMapper } from '../../utils';

type Props = {
  isReadOnly?: boolean;
  txCenterName: string;
  onSelectionChangeAction: (key: Key | null, onChange: (val: Key | null) => void) => void;
  setValueAction: (value: Key | null, list: unknown[]) => void;
};

export const TXCenterSingleSelect = React.memo(
  ({ isReadOnly, txCenterName, onSelectionChangeAction, setValueAction }: Props) => {
    const { control } = useFormContext();

    const {
      searchValue,
      list: infiniteDropdownTxCentersList,
      isLoading,
      onSearchValue,
      setAutocompleteOpen,
      itemsScrollerRef,
      onItemSelectChange,
    } = useInfiniteDropdown({
      setValue: (value: Key | null) => setValueAction(value, infiniteDropdownTxCentersList),
      listQuery: useInfiniteTXCentersListQuery,
      mapper: txCenterMapper,
      defaultSortOrder: { orderBy: 'name', asc: true },
      initialName: txCenterName || '',
      clearOnSearch: false,
    });

    return (
      <AdminFormRow title="TX Center" isRequired inputWrapperClass="max-w-160">
        <Controller
          name="txCenterId"
          control={control}
          render={({ field: { ref, value, onChange, ...field }, fieldState: { invalid, error } }) => (
            <Autocomplete
              allowsCustomValue={false}
              placeholder="Select a TX Center"
              aria-label="TX Centers"
              inputValue={searchValue || txCenterName}
              scrollRef={itemsScrollerRef}
              items={infiniteDropdownTxCentersList}
              isLoading={isLoading}
              onInputChange={(text) => {
                onSearchValue(text);
                if (!text) {
                  onChange('');
                  onItemSelectChange(null);
                }
              }}
              isInvalid={invalid}
              isDisabled={isReadOnly}
              errorMessage={error?.message}
              onOpenChange={(isOpen) => setAutocompleteOpen(isOpen)}
              selectedKey={value || null}
              onSelectionChange={(key) => {
                onItemSelectChange(key);
                onSelectionChangeAction(key, onChange);
              }}
              {...field}
            >
              {renderAutocompleteItem}
            </Autocomplete>
          )}
        />
      </AdminFormRow>
    );
  },
);

TXCenterSingleSelect.displayName = 'TXCenterSingleSelect';
